import { EMPTY, of } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { catchError, map, switchMap, mergeMap } from 'rxjs/operators';

import {
  fetchUserData,
  login as loginService,
  logout as logoutService,
  startUserEmulation,
  getUserSessionInfo
} from 'api/user/service';
import { getConfigSettings } from 'api/config/service';
import { setInfuseConfig } from 'logic/store/infuse/actions';

import { STORAGE_KEYS } from 'constants.js';

import {
  logout,
  getUserFailed,
  loginFailed,
  updateUser,
  setCurrentUser,
  setThirdPartyScripts
} from './actions';

/**
 * Set user data, based on supplied token.
 *
 * @param {string} token
 */
const setUser = (token) =>
  token
    ? updateUser({ authenticationId: token, emulationSession: false, bankUser: true })
    : setCurrentUser(null);

export const AUTH_GET_USER = (action$) =>
  action$.pipe(
    switchMap(({ data }) =>
      fromPromise(fetchUserData(data)).pipe(
        map((user) => setCurrentUser(user)),
        catchError((error) => of(getUserFailed(error)))
      )
    )
  );

export const AUTH_SET_CURRENT_USER = (action$) =>
  action$.pipe(
    switchMap(() =>
      getConfigSettings().pipe(
        map(({ data }) => {
          return setThirdPartyScripts({
            liveEngageKey: data.LIVE_ENGAGE,
            enableGlia: data.ENABLE_GLIA === '1'
          });
        })
      )
    )
  );

export const AUTH_LOGOUT = (action$) =>
  action$.pipe(
    switchMap(({ data: params }) => {
      sessionStorage.removeItem(STORAGE_KEYS.EMULATION_CUSTOMER_NAME);
      sessionStorage.removeItem(STORAGE_KEYS.EMULATION_TOKEN);
      sessionStorage.removeItem('firstTimeSetup');
      setInfuseConfig({ store: { closed: 0 } }, true);
      return fromPromise(logoutService(params)).pipe(
        map(({ data, success }) =>
          success && data?.emulatorSessionId ? setUser(data.emulatorSessionId) : setUser(null)
        ),
        catchError(() => of(setUser(null)))
      );
    })
  );

export const AUTH_LOGOUT_ALL = (action$) =>
  action$.pipe(
    switchMap(({ data: params }) => {
      sessionStorage.removeItem(STORAGE_KEYS.EMULATION_CUSTOMER_NAME);
      sessionStorage.removeItem(STORAGE_KEYS.EMULATION_TOKEN);
      sessionStorage.removeItem('firstTimeSetup');
      return fromPromise(logoutService(params)).pipe(
        map(() => logout()),
        catchError(() => EMPTY)
      );
    })
  );

export const AUTH_LOGIN = (action$) =>
  action$.pipe(
    switchMap(({ data }) =>
      fromPromise(loginService(data)).pipe(
        map((user) => setCurrentUser(user)),
        catchError((error) => of(loginFailed(error)))
      )
    )
  );

export const AUTH_START_EMULATION = (action$) =>
  action$.pipe(
    switchMap(({ data }) =>
      fromPromise(startUserEmulation(data)).pipe(
        mergeMap((user) =>
          fromPromise(getUserSessionInfo(user.authenticationId)).pipe(
            map(({ data: userInfo }) => {
              const { authenticationId } = userInfo;
              sessionStorage.setItem(STORAGE_KEYS.EMULATION_CUSTOMER_NAME, data.customerName);
              sessionStorage.setItem(STORAGE_KEYS.EMULATION_TOKEN, authenticationId);
              return setCurrentUser(
                {
                  ...userInfo,
                  emulationSession: true
                },
                true
              );
            })
          )
        )
      )
    )
  );
